/**
 * The call's media budget on a phone (3 Nov, job 2). A phone sends its camera at most 640x360, 15 frames a
 * second and 600 kbps. While a phone shows the match or a share on its stage, the other side's camera is a
 * small tile there, so it asks that side to send its camera low (a quarter of the pixels, 250 kbps) over
 * the room's data channel; the wrapper has no simulcast layers to pick from. A switch in localStorage turns
 * it off (db_call_budget = "off"), for measuring before and after on one build, or treats any device as a
 * phone ("phone"). Pure, so tested.
 */
export const PHONE_SEND = { width: 640, height: 360, fps: 15, kbps: 600 } as const;
export const LOW_SEND = { kbps: 250, scale: 2, fps: 15 } as const;

/** A phone: a touch screen whose short side is under 768. */
export function isPhone(p: { coarse: boolean; shortSide: number }): boolean {
  return p.coarse && p.shortSide < 768;
}

/** The camera to ask for: a phone's at the budget's size and rate, any other the sensor's landscape size. */
export function cameraAsk(phone: boolean): MediaTrackConstraints {
  return phone
    ? { width: { ideal: PHONE_SEND.width }, height: { ideal: PHONE_SEND.height }, frameRate: { ideal: PHONE_SEND.fps, max: PHONE_SEND.fps } }
    : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };
}

/**
 * What the camera's sender may use: a phone's cap (its long side brought to 640, 15 fps, 600 kbps), and
 * on top of it, while the other side asks for it, the low send. Nothing at all (undefined fields) when
 * neither applies, so the encoder chooses as before.
 */
export function cameraEncoding(p: { phone: boolean; farWantsLow: boolean; trackW: number | null; trackH: number | null }): {
  maxBitrate?: number; maxFramerate?: number; scaleResolutionDownBy?: number;
} {
  if (!p.phone && !p.farWantsLow) return {};
  const long = Math.max(p.trackW ?? 0, p.trackH ?? 0);
  let scale = p.phone && long > PHONE_SEND.width ? long / PHONE_SEND.width : 1;
  let kbps: number = p.phone ? PHONE_SEND.kbps : Infinity;
  if (p.farWantsLow) { scale *= LOW_SEND.scale; kbps = Math.min(kbps, LOW_SEND.kbps); }
  return {
    maxBitrate: kbps * 1000,
    maxFramerate: p.phone || p.farWantsLow ? PHONE_SEND.fps : undefined,
    scaleResolutionDownBy: Math.round(scale * 100) / 100,
  };
}

/** A phone with the match or a share on its stage asks for the other side's camera low. */
export function wantsFarLow(p: { phone: boolean; budgetOn: boolean; matchOpen: boolean; shareOnStage: boolean }): boolean {
  return p.budgetOn && p.phone && (p.matchOpen || p.shareOnStage);
}

/** The ask, over the room's data channel. */
export interface CamQualityWire { t: 'camq'; low: boolean }
export function isCamQualityWire(d: unknown): d is CamQualityWire {
  return !!d && typeof d === 'object' && (d as { t?: unknown }).t === 'camq' && typeof (d as { low?: unknown }).low === 'boolean';
}

/** The budget's switch: on unless localStorage says "off". */
export function budgetOn(read: () => string | null): boolean {
  try { return read() !== 'off'; } catch { return true; }
}
